// AlgoliaInstantSearchRouter.ts
import { getIndexName } from '../../constants';
import { normalizeFilterKey } from '../facets/normalize-filter-value';

// Flat uiState type for the router
export type IndexUiState = {
  query?: string;
  page?: number;
  refinementList?: Record<string, string[]>;
  range?: Record<string, string | { min?: number | string; max?: number | string }>;
  toggle?: Record<string, boolean>;
};

export interface RouterConfig {
  locale: '' | 'en' | 'fi' | 'sv'; // '' == english (no prefix)
  basePath?: string;
}

export interface SearchPathSegments {
  locale: '' | 'en' | 'fi' | 'sv';
  jobs?: string;
  location?: string;
}

// ────────────────────────────────────────────────
// Locale helpers
// ────────────────────────────────────────────────
function jobsSegment(locale: '' | 'en' | 'fi' | 'sv'): string {
  switch (locale) {
    case 'fi':
      return 'tyopaikat';
    case 'sv':
      return 'lediga-jobb';
    default:
      return 'jobs';
  }
}

function locationSegment(locale: '' | 'en' | 'fi' | 'sv'): string {
  switch (locale) {
    case 'fi':
      return 'alue';
    case 'sv':
      return 'omrade';
    default:
      return 'location';
  }
}

function encodeLocation(loc: string): string {
  return loc.replace(/[,\s]+/g, '_');
}
function decodeLocation(seg: string): string {
  return seg.replace(/_/g, ' ').replace(/\s+/g, ', ');
}

// ────────────────────────────────────────────────
// Core router
// ────────────────────────────────────────────────
export class AlgoliaInstantSearchRouter {
  private readonly config: RouterConfig;
  private readonly indexName: string;

  constructor(config: RouterConfig) {
    this.config = config;
    this.indexName = getIndexName(config.locale || 'en');
  }

  /** Build a URL from InstantSearch routeState */
  createUrl(state: IndexUiState): string {
    const { locale, basePath = '' } = this.config;

    let path = basePath;

    // Locale prefix (skip for english/empty)
    if (locale && locale !== 'en') {
      path += `/${locale}`;
    }

    const loc = state.refinementList?.location?.[0];
    const hasOtherState = this.hasOtherState(state);

    if (loc) {
      path += `/${jobsSegment(locale)}/${locationSegment(locale)}/${encodeLocation(loc)}`;
    } else if (hasOtherState) {
      path += `/${jobsSegment(locale)}`;
    }

    const params = this.indexUiStateToSearchParams(state);

    // Additional locations
    const locs = state.refinementList?.location ?? [];
    if (locs.length > 1) {
      locs.slice(1).forEach((l) => params.append('location', encodeLocation(l)));
    }

    const qs = params.toString();
    return qs ? `${path}?${qs}` : path || '/';
  }

  /** Parse a URL back into InstantSearch state */
  parseUrl(url: string): { indexUiState: IndexUiState; pathSegments: SearchPathSegments } {
    const u = new URL(url, 'http://localhost'); // dummy base
    const segs = u.pathname.split('/').filter(Boolean);

    // base defaults
    let locale: '' | 'en' | 'fi' | 'sv' = '';

    if (segs[0] === 'fi' || segs[0] === 'sv') {
      locale = segs[0] as any;
      segs.shift();
    }

    const result: SearchPathSegments = { locale };
    const jobsTok = jobsSegment(locale);
    const locTok = locationSegment(locale);

    const jobsIdx = segs.findIndex((s) => s === jobsTok);
    if (jobsIdx >= 0) {
      result.jobs = segs[jobsIdx];
      const legacyLocTok = locTok === 'omrade' ? 'område' : locTok;
      if (
        segs[jobsIdx + 1] &&
        (segs[jobsIdx + 1] === locTok ||
          segs[jobsIdx + 1] === legacyLocTok ||
          segs[jobsIdx + 1] === 'omr%C3%A5de') &&
        segs[jobsIdx + 2]
      ) {
        result.location = decodeLocation(segs[jobsIdx + 2]);
      }
    }

    const state = this.searchParamsToIndexUiState(u.searchParams);
    if (result.location) {
      state.refinementList ??= {};
      state.refinementList.location = [
        result.location,
        ...(state.refinementList.location ?? []),
      ];
    }

    return { indexUiState: state, pathSegments: result };
  }

  // ───────── helpers ─────────

  private hasOtherState(s: IndexUiState): boolean {
    if (s.query && s.query.trim()) return true;
    if (s.page && s.page > 1) return true;

    const refinements = Object.entries(s.refinementList ?? {}).filter(([k, v]) => k !== 'location' && v.length);
    if (refinements.length) return true;

    if (s.range && Object.keys(s.range).length) return true;
    if (s.toggle && Object.values(s.toggle).some(Boolean)) return true;
    return false;
  }

  private indexUiStateToSearchParams(state: IndexUiState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.query) params.set('search', state.query);
    if (state.page && state.page > 1) params.set('page', String(state.page));

    Object.entries(state.refinementList ?? {}).forEach(([k, vals]) => {
      if (k === 'location') return;
      vals.forEach((v) => params.append(k, v));
    });

    Object.entries(state.range ?? {}).forEach(([attr, val]) => {
      if (typeof val === 'string') {
        const [min, max] = val.split(':');
        if (min) params.set(`${attr}_min`, min);
        if (max) params.set(`${attr}_max`, max);
      } else {
        if (val?.min !== undefined) params.set(`${attr}_min`, String(val.min));
        if (val?.max !== undefined) params.set(`${attr}_max`, String(val.max));
      }
    });

    Object.entries(state.toggle ?? {}).forEach(([k, v]) => {
      if (v) params.set(k, 'true');
    });

    return params;
  }

  private searchParamsToIndexUiState(params: URLSearchParams): IndexUiState {
    const state: IndexUiState = {};
    const refinements: Record<string, string[]> = {};

    const q = params.get('search');
    if (q) state.query = q;

    const p = params.get('page');
    if (p) state.page = parseInt(p, 10);

    // List of tracking/marketing parameters to ignore (not treated as search filters)
    const ignoredParams = new Set([
      // UTM parameters (Google Ads, marketing campaigns)
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      // Google Click ID
      'gclid',
      // Google Browser ID (used by Google crawlers)
      'gbraid',
      'wbraid',
      // Facebook Click ID
      'fbclid',
      // Microsoft Click ID
      'msclkid',
      // Other common tracking parameters
      'ref',
      'source',
      'medium',
      'campaign',
      'term',
      'content',
      // Google Analytics
      '_ga',
      '_gid',
      // Other analytics/tracking
      'mc_cid',
      'mc_eid',
      'utm_id',
      'utm_source_platform',
      'utm_creative_format',
      'utm_marketing_tactic',
    ]);

    params.forEach((value, key) => {
      // Normalize parameter key to lowercase only for checking ignored/tracking params
      const normalizedKey = normalizeFilterKey(key);
      const filterKey = key;
      
      // Skip ignored tracking parameters
      if (ignoredParams.has(normalizedKey)) {
        return;
      }
      
      if (normalizedKey === 'search' || normalizedKey === 'page') return;

      if (normalizedKey.endsWith('_min') || normalizedKey.endsWith('_max')) {
        const attr = normalizedKey.replace(/_(min|max)$/, '');
        state.range ??= {};
        const existing = (state.range[attr] as any) || {};
        if (normalizedKey.endsWith('_min')) existing.min = value;
        else existing.max = value;
        state.range[attr] = existing;
        return;
      }

      if (value === 'true') {
        state.toggle ??= {};
        state.toggle[normalizedKey] = true;
        return;
      }

      refinements[filterKey] ??= [];
      // Keep filter values as-is (except for decoded locations) to avoid mismatches with Algolia facet values
      const rawValue = normalizedKey === 'location' ? decodeLocation(value) : value;
      refinements[filterKey].push(rawValue);
    });

    if (Object.keys(refinements).length) {
      state.refinementList = refinements;
    }

    return state;
  }
}

// Factory
export function createAlgoliaRouter(config: RouterConfig) {
  const r = new AlgoliaInstantSearchRouter(config);
  return {
    createUrl: (s: IndexUiState) => r.createUrl(s),
    parseUrl: (u: string) => r.parseUrl(u),
    getIndexName: () => (r as any).indexName as string,
    getConfig: () => (r as any).config as RouterConfig,
  };
}